import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import type { ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  error: string | null;
  signUp: (email: string, password: string) => Promise<{ success: boolean; error?: string; user?: User | null }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; user?: User | null }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  /** Sends a magic-link / OTP email. The user follows the link in their
   *  email client (or types the code if Supabase email template uses
   *  `{{ .Token }}`). Works in all webviews — no Google policy block.
   *  Used as the primary fallback when isInAppBrowser() is true. */
  signInWithEmailOtp: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyEmailOtp: (email: string, token: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<{ success: boolean; error?: string }>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase || !isSupabaseConfigured) {
      setState({ user: null, session: null, loading: false });
      return;
    }

    // Single subscription for the entire app. onAuthStateChange fires
    // INITIAL_SESSION on setup which reads + refreshes the stored session.
    // Previously each component had its own subscription, causing concurrent
    // token refresh attempts that triggered Supabase's "compromised refresh
    // token" revocation — destroying the session on every page reload.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setState({
        user: session?.user ?? null,
        session,
        loading: false
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined
      }
    });

    if (signUpError) {
      setError(signUpError.message);
      return { success: false, error: signUpError.message };
    }

    return { success: true, user: data.user };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (signInError) {
      setError(signInError.message);
      return { success: false, error: signInError.message };
    }

    return { success: true, user: data.user };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      return { success: false, error: oauthError.message };
    }

    // OAuth redirects — success is handled by onAuthStateChange after redirect
    return { success: true };
  }, []);

  const signInWithEmailOtp = useCallback(async (email: string) => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // The email carries both the link and the one-time code; the code is what
        // signs in an installed PWA, which can't capture links (#127).
        shouldCreateUser: true,
        emailRedirectTo: window.location.href,
      },
    });
    if (otpError) {
      setError(otpError.message);
      return { success: false, error: otpError.message };
    }
    return { success: true };
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { error: verifyError } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    if (verifyError) {
      setError(verifyError.message);
      return { success: false, error: verifyError.message };
    }
    return { success: true };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (resetError) {
      setError(resetError.message);
      return { success: false, error: resetError.message };
    }
    return { success: true };
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) {
      const msg = 'Supabase not configured';
      setError(msg);
      return { success: false, error: msg };
    }
    setError(null);
    // H-105d: global scope so the token is revoked across all tabs
    // and devices, not just the current browser. Previously signOut
    // used the default 'local' scope which left other tabs signed in.
    const { error: signOutError } = await supabase.auth.signOut({ scope: 'global' });

    if (signOutError) {
      setError(signOutError.message);
      return { success: false, error: signOutError.message };
    }

    return { success: true };
  }, []);

  const value: AuthContextValue = {
    user: state.user,
    session: state.session,
    loading: state.loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithEmailOtp,
    verifyEmailOtp,
    resetPassword,
    signOut,
    isAuthenticated: !!state.user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
